import { Injectable } from "@angular/core";

import {
    AgentKey,
    ChatTurn,
    ConversationSummary,
    HistoryListResponse,
    UchatSource,
    UchatTurn
} from "../model/uchat.model";
import { AgentCatalogueService } from "./agent-catalogue.service";

/** One row in the conversation sidebar. */
export interface ConversationRow {
    historyId: string;
    title: string;
    /** The title the server last reported, or null if it has never titled this conversation. Lets "applyTitle" tell a real rename apart from a no-op. */
    serverTitle: string | null;
    agent: AgentKey;
    modifiedAt: number;
}

/** A dated bucket of rows, e.g. "Previous 7 days". */
export interface ConversationGroup {
    label: string;
    rows: ConversationRow[];
}

const DAY = 86400000;

/**
 * Title overrides live in sessionStorage
 */
const TITLE_OVERRIDE_KEY = "pawsAgentChatTitles";
const THREAD_CACHE_PREFIX = "pawsAgentChatThread:";
const CURRENT_HISTORY_ID = "currentChatHistoryId"

/** Shown when the backend has not titled a conversation. Exported so the component agrees. */
export const UNTITLED_CONVERSATION = "Untitled conversation";

/**
 * Sidebar state and the local half of the "server + local cache" decision.
 */
@Injectable({
    providedIn: 'root'
})
export class ConversationStoreService {
    constructor(
        private agentCatalogueService: AgentCatalogueService
    ){

    }

    public rows: ConversationRow[] = [];

    /** Replace the rows from a fresh GET /chat/history, newest first. */
    public setFromResponse( response: HistoryListResponse ): void {
        let overrides = this.readOverrides();
        let conversations = response == null || response.conversations == null ? [] : response.conversations;
        this.rows = conversations
            .map( (c: ConversationSummary) => this.toRow(c, overrides) )
            .sort( (a: ConversationRow, b: ConversationRow) => b.modifiedAt - a.modifiedAt );
    }

    private toRow( summary: ConversationSummary, overrides: { [id: string]: string } ): ConversationRow {
        let override = overrides[summary.history_id];
        let title = override != null && override !== ""
            ? override
            : ( summary.title != null && summary.title.trim() !== "" ? summary.title : UNTITLED_CONVERSATION );
        return {
            historyId: summary.history_id,
            title: title,
            serverTitle: summary.title == null ? null : summary.title,
            agent: summary.agent,
            modifiedAt: this.parseModifiedAt(summary)
        };
    }

    /**
     * "modify_datetime" (falling back to "create_datetime", falling back to 0) as
     * epoch millis. 
     */
    private parseModifiedAt( summary: ConversationSummary ): number {
        let modified = new Date(summary.modify_datetime).getTime();
        if ( !isNaN(modified) ) {
            return modified;
        }
        let created = new Date(summary.create_datetime).getTime();
        if ( !isNaN(created) ) {
            return created;
        }
        return 0;
    }

    /** Which dated bucket a timestamp belongs to. */
    public groupOf( timestamp: number, now: number ): string {
        let age = now - timestamp;
        if ( new Date(timestamp).toDateString() === new Date(now).toDateString() ) {
            return "Today";
        }
        if ( age < (7 * DAY) ) {
            return "Previous 7 days";
        }
        if ( age < (30 * DAY) ) {
            return "Previous 30 days";
        }
        return "Older";
    }

    /**
     * Rows matching "query", bucketed by age. Empty buckets are omitted.
     */
    public groups( query: string, now: number ): ConversationGroup[] {
        let needle = (query == null ? "" : query).trim().toLowerCase();
        let matches = this.rows.filter( (r: ConversationRow) => {
            if ( needle === "" ) {
                return true;
            }
            return r.title.toLowerCase().includes(needle)
                || this.agentCatalogueService.agentLabel(r.agent).toLowerCase().includes(needle);
        } );

        const LABEL_ORDER = ["Today", "Previous 7 days", "Previous 30 days", "Older"];
        let groups: ConversationGroup[] = [];
        LABEL_ORDER.forEach( (label: string) => {
            let rowsForLabel = matches.filter( (row: ConversationRow) => this.groupOf(row.modifiedAt, now) === label );
            if ( rowsForLabel.length > 0 ) {
                groups.push( { label: label, rows: rowsForLabel } );
            }
        } );
        return groups;
    }

    public row( historyId: string ): ConversationRow | null {
        let found = this.rows.find( (r: ConversationRow) => r.historyId === historyId );
        return found === undefined ? null : found;
    }

    /**
     * Set a title locally and remember it, so a rename survives a list refresh.
     */
    public applyTitle( historyId: string, title: string ): void {
        let row = this.row(historyId);
        if ( row !== null ) {
            row.title = title;
        }
        let overrides = this.readOverrides();
        if ( row !== null && row.serverTitle === title ) {
            delete overrides[historyId];
        } else {
            overrides[historyId] = title;
        }
        this.writeOverrides(overrides);
    }

    /**
     * Record what the server calls this conversation.
     */
    public applyServerTitle( historyId: string, title: string | null ): boolean {
        let row = this.row(historyId);
        if ( row === null || title == null || title.trim() === "" ) {
            return false;
        }
        row.serverTitle = title;
        if ( row.title !== UNTITLED_CONVERSATION ) {
            return false;
        }
        row.title = title;
        return true;
    }

    public remove( historyId: string ): void {
        this.rows = this.rows.filter( (r: ConversationRow) => r.historyId !== historyId );
        let overrides = this.readOverrides();
        delete overrides[historyId];
        this.writeOverrides(overrides);
        this.safely( () => sessionStorage.removeItem(THREAD_CACHE_PREFIX + historyId) );
    }

    /** Mark a conversation as just-used and re-sort. */
    public touch( historyId: string, timestamp: number ): void {
        let row = this.row(historyId);
        if ( row === null ) {
            return;
        }
        row.modifiedAt = timestamp;
        this.rows = [...this.rows].sort( (a: ConversationRow, b: ConversationRow) => b.modifiedAt - a.modifiedAt );
    }

    /** Prepend a conversation that was just created by the first message of a chat. */
    public prepend( row: ConversationRow ): void {
        this.rows = [row].concat( this.rows.filter( (r: ConversationRow) => r.historyId !== row.historyId ) );
    }

    /** Server turns -> view turns. Any role other than "user" renders as the agent. */
    public toChatTurns( turns: UchatTurn[] ): ChatTurn[] {
        let list = turns == null ? [] : turns;
        return list.map( (t: UchatTurn) => ( {
            role: t.role === "user" ? "user" : "assistant",
            content: t.content == null ? "" : t.content,
            at: t.at,
            rating: t.rating,
            id: t.id,
            sources: (t.sources == null ? [] : t.sources) as UchatSource[],
            streaming: false,
            failed: false
        } as ChatTurn ) );
    }

    public cacheTurns( historyId: string, turns: ChatTurn[] ): void {
        this.safely( () => sessionStorage.setItem(THREAD_CACHE_PREFIX + historyId, JSON.stringify(turns)) );
    }

    /**
     * Read a cached thread back, forcing "streaming" off.
     */
    public cachedTurns( historyId: string ): ChatTurn[] | null {
        let raw: string | null = this.safelyRead( () => sessionStorage.getItem(THREAD_CACHE_PREFIX + historyId) );
        if ( raw == null ) {
            return null;
        }
        try {
            let parsed = JSON.parse(raw);
            if ( !Array.isArray(parsed) ) {
                return null;
            }
            return parsed.map( (t: ChatTurn) => ( { ...t, streaming: false } as ChatTurn ) );
        } catch ( e ) {
            return null;
        }
    }

    /**
     * Drop the title-override map entirely.
     */
    public clearOverrides(): void {
        this.safely( () => sessionStorage.removeItem(TITLE_OVERRIDE_KEY) );
    }

    /** Drop every cached thread. Used on logout-ish transitions. */
    public clearCache(): void {
        this.safely( () => {
            let keys: string[] = [];
            for ( let i = 0; i < sessionStorage.length; i++ ) {
                let key = sessionStorage.key(i);
                if ( key !== null && key.startsWith(THREAD_CACHE_PREFIX) ) {
                    keys.push(key);
                }
            }
            keys.forEach( (k: string) => sessionStorage.removeItem(k) );
        } );
    }

    private readOverrides(): { [id: string]: string } {
        let raw: string | null = this.safelyRead( () => sessionStorage.getItem(TITLE_OVERRIDE_KEY) );
        if ( raw == null ) {
            return {};
        }
        try {
            let parsed = JSON.parse(raw);
            return parsed != null && typeof parsed === "object" ? parsed : {};
        } catch ( e ) {
            return {};
        }
    }

    private writeOverrides( overrides: { [id: string]: string } ): void {
        this.safely( () => sessionStorage.setItem(TITLE_OVERRIDE_KEY, JSON.stringify(overrides)) );
    }

    /** Storage throws in private-browsing modes; a missing cache is never fatal. */
    private safely( action: () => void ): void {
        try {
            action();
        } catch ( e ) {
            // Cache is an optimisation, not a requirement.
        }
    }

    /** As "safely", but for reads that need to hand back a value (or null on failure). */
    private safelyRead<T>( action: () => T ): T | null {
        try {
            return action();
        } catch ( e ) {
            return null;
        }
    }

    public setHistoryId(historyId:string){
        sessionStorage.setItem(CURRENT_HISTORY_ID, historyId);
    }

    public getHistoryId():string{
        return sessionStorage.getItem(CURRENT_HISTORY_ID);
    }

    public removeHistoryId(){
        sessionStorage.removeItem(CURRENT_HISTORY_ID);
    }

    public hasChatHistoryId(): boolean {
        return ( sessionStorage.getItem(CURRENT_HISTORY_ID) != undefined );
    }
}
